// frontend/apps/widgets/src/widgets/common/hooks/useWidgetError.js
import {useState, useCallback} from 'react';

export const WIDGET_ERROR_TYPES = {
  API_KEY_MISSING: 'API_KEY_MISSING',
  API_KEY_INVALID: 'API_KEY_INVALID',
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  STRIPE_NOT_CONFIGURED: 'STRIPE_NOT_CONFIGURED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

export const WIDGET_ERROR_MESSAGES = {
  [WIDGET_ERROR_TYPES.API_KEY_MISSING]: {
    title: 'Configuration manquante',
    message: 'La configuration du widget est incomplète.',
    details: 'Veuillez contacter l\'administrateur du site.',
  },
  [WIDGET_ERROR_TYPES.API_KEY_INVALID]: {
    title: 'Configuration incorrecte',
    message: 'Les paramètres de configuration ne sont pas valides.',
    details: 'Veuillez contacter l\'administrateur du site.',
  },
  [WIDGET_ERROR_TYPES.COMPANY_NOT_FOUND]: {
    title: 'Établissement introuvable',
    message: 'L\'établissement demandé n\'existe pas.',
    details: 'Veuillez vérifier l\'URL ou contacter l\'administrateur.',
  },
  [WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED]: {
    title: 'Paiement non configuré',
    message: 'Ce commerce n\'a pas encore configuré son système de paiement.',
    details: 'Veuillez contacter directement l\'établissement.',
  },
  [WIDGET_ERROR_TYPES.NETWORK_ERROR]: {
    title: 'Erreur de connexion',
    message: 'Impossible de se connecter au service.',
    details: 'Veuillez vérifier votre connexion internet et réessayer.',
  },
  [WIDGET_ERROR_TYPES.UNKNOWN_ERROR]: {
    title: 'Erreur inattendue',
    message: 'Une erreur inattendue est survenue.',
    details: 'Veuillez réessayer dans quelques instants.',
  },
};

/**
 * Hook for complex error management (not used in current implementation)
 * Kept for potential future use
 * @return {Object} An object containing error state and error handling functions
 */
export function useWidgetError() {
  const [error, setError] = useState(null);

  const handleError = useCallback((apiError) => {
    console.error('[Widget Error Handler]:', apiError);
    // Implementation kept for future use
    setError(apiError);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    error,
    handleError,
    clearError,
    hasError: !!error,
  };
}

/**
 * Creates a synthetic Stripe error object for unified error handling
 * @param {string} widgetType - Type of widget (reservations, giftvouchers, etc.)
 * @return {Object} Synthetic error object
 */
export function createStripeNotConfiguredError(widgetType = 'generic') {
  return {
    isStripeError: true,
    widgetType,
    message: 'Stripe account not configured',
    response: {
      status: 422,
      data: {
        code: 'STRIPE_NOT_CONFIGURED',
        message: 'Stripe account not configured',
      },
    },
  };
}

/**
 * Utility function to get error information from API errors
 * This is the main function used by widget components
 * @param {Error} error - The API error object
 * @return {Object} Error information with title, message, and details
 */
export function getErrorInfo(error) {
  if (!error) return null;

  // Check if it's a synthetic Stripe error created by createStripeNotConfiguredError
  if (error?.isStripeError) {
    return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED];
  }

  // Check if it's a client-side error first (before API call)
  if (error?.message) {
    const message = error.message.toLowerCase();

    // Detect client-side API key issues
    if (message.includes('api key is missing') ||
        message.includes('missing companyid or apikey') ||
        message.includes('api key manquante')) {
      return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_MISSING];
    } else if (message.includes('api key') &&
             (message.includes('invalid') || message.includes('invalide'))) {
      return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
    }
  }

  // If it's an HTTP error response, analyze the status
  if (error?.response) {
    const {status, data} = error.response;

    switch (status) {
      case 401:
        return data?.code === 'API_KEY_MISSING' ?
          WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_MISSING] :
          WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
      case 403:
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.API_KEY_INVALID];
      case 404:
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.COMPANY_NOT_FOUND];
      case 422:
        if (data?.code === 'STRIPE_NOT_CONFIGURED') {
          return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.STRIPE_NOT_CONFIGURED];
        }
        break;
      default:
        return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.NETWORK_ERROR];
    }
  }

  // If it's a network error without response
  if (error?.code === 'NETWORK_ERROR') {
    return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.NETWORK_ERROR];
  }

  return WIDGET_ERROR_MESSAGES[WIDGET_ERROR_TYPES.UNKNOWN_ERROR];
}
